<template>
  <v-layout>
    <v-dialog v-bind:value="dialog" @click:outside="$emit('updateDialog', false)" max-width="640px">
      <template v-slot:activator="{ on }">
        <v-btn color="primary" class="mb-5" dark v-on="on" @click="$emit('updateDialog', true)">{{ button_name }}</v-btn>
      </template>

      <v-card>
        <v-form v-on:submit.prevent="createItem">
          <v-card-title>
            <span class="headline">{{ modal_title }}</span>
          </v-card-title>

          <v-card-text>
            <div class="row">
              <div class="col-md-12">
                <v-text-field
                  data-vv-as="nombre"
                  label="Nombre*"
                  name="name"
                  v-model="item.name"
                  v-validate="'required'"
                  :disabled="isViewModal()"
                ></v-text-field>
                <span class="red--text">{{ errors.first("name") }}</span>

                <v-combobox
                  v-model="item.tipo"
                  data-vv-as="tipo"
                  name="tipo"
                  item-text="name"
                  item-value="id"
                  :items="tipos"
                  label="Tipo*"
                  autocomplete="off"
                  v-validate="'required|combobox_required'"
                  :disabled="isViewModal()"
                  return-object
                  @change="onTipoChange"
                ></v-combobox>
                <span class="red--text">{{ errors.first("tipo") }}</span>

                <v-combobox
                  v-model="item.parent"
                  name="parent"
                  item-text="name"
                  item-value="id"
                  :items="conceptos"
                  label="Concepto padre (opcional)"
                  autocomplete="off"
                  clearable
                  :disabled="isViewModal()"
                  return-object
                ></v-combobox>

                <v-text-field
                  label="Código (opcional)"
                  name="code"
                  v-model="item.code"
                  :disabled="isViewModal()"
                ></v-text-field>

                <v-checkbox label="Habilitado" :disabled="isViewModal()" v-model="item.enabled"></v-checkbox>
              </div>
            </div>

            <!-- Cuentas de resultado por emisor -->
            <template v-if="emitters.length">
              <v-divider class="mb-3"></v-divider>
              <div class="caption font-weight-bold text-uppercase grey--text text--darken-1 mb-1">
                Cuenta de resultado por emisor
              </div>
              <div class="caption grey--text mb-3">
                Define a qué cuenta contable imputa este concepto en cada emisor.
                Un concepto de <strong>Ingreso</strong> necesita cuenta de venta; uno de <strong>Egreso</strong>, cuenta de compra;
                uno de <strong>Ambos</strong>, al menos una (imputa distinto según aparezca en una factura o en un gasto;
                el lado sin cuenta usa la cuenta por defecto).
                Obligatoria en los emisores con contabilidad habilitada.
              </div>

              <div v-if="loading_accounts" class="text-center py-3">
                <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
              </div>

              <div v-else>
                <div v-for="emitter in emitters" :key="'em-' + emitter.id" class="mb-2">
                  <div class="body-2 font-weight-medium">{{ emitterLabel(emitter) }}</div>
                  <div class="row">
                    <div class="col-md-6" v-if="showsSide('venta')">
                      <v-combobox
                        v-model="mappings[emitter.id].venta"
                        :item-text="accountLabel"
                        item-value="id"
                        :items="accountsForEmitter(emitter.id, 'venta')"
                        label="Cuenta de venta (Ingresos)"
                        autocomplete="off"
                        clearable
                        return-object
                        :disabled="isViewModal()"
                        :no-data-text="'Sin cuentas de Ingresos en este emisor'"
                      ></v-combobox>
                    </div>
                    <div class="col-md-6" v-if="showsSide('compra')">
                      <v-combobox
                        v-model="mappings[emitter.id].compra"
                        :item-text="accountLabel"
                        item-value="id"
                        :items="accountsForEmitter(emitter.id, 'compra')"
                        label="Cuenta de compra (Egresos)"
                        autocomplete="off"
                        clearable
                        return-object
                        :disabled="isViewModal()"
                        :no-data-text="'Sin cuentas de Egresos en este emisor'"
                      ></v-combobox>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="error" text @click="$emit('updateDialog', false)">Cerrar</v-btn>
            <v-btn color="success" text type="submit" v-if="!isViewModal()" :loading="loading">{{ formBtn }}</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import ModalGenericMixin from "@/mixins/ModalGenericMixin.vue";
import ConstantSections from "@/constants/sections";
import { generalService } from "@/libs/ws/generalService";
import { emmiterService } from "@/libs/ws/emmiterService";

export default {
  mixins: [ModalGenericMixin],
  props: {
    parent: Object,
    service: Object,
    modal_title: String,
    dialog: Boolean,
    item: Object,
    button_name: String,
    formBtn: String,
  },
  data() {
    return {
      show: false,
      conceptos: [],
      tipos: [
        { id: "I", name: "Ingreso" },
        { id: "E", name: "Egreso" },
        { id: "A", name: "Ambos" },
      ],
      emitters: [],
      resultAccounts: [],
      loading_accounts: false,
      mappings: {},
    };
  },
  watch: {
    item() {
      this.$emit("updateItem", this.item);
    },
    dialog(val) {
      this.errors.clear();
      if (val) this.initAccountingSection();
    },
  },
  mounted() {
    generalService.filter({ enabled: "all" }, ConstantSections.SECTION_CONCEPTOS).then((response) => {
      this.conceptos = response.result;
    });
    this.loadEmittersAndAccounts();
  },
  methods: {
    isViewModal() {
      return this.formBtn == "";
    },
    tipoId() {
      if (!this.item.tipo) return null;
      return typeof this.item.tipo === "object" ? this.item.tipo.id : this.item.tipo;
    },
    accountLabel(account) {
      if (!account) return "";
      return account.code ? account.code + " - " + account.name : account.name;
    },
    emitterLabel(emitter) {
      return emitter.contabilidad_habilitada ? emitter.name + " *" : emitter.name;
    },
    loadEmittersAndAccounts() {
      this.loading_accounts = true;
      const p1 = emmiterService.filter({ enabled: true }).then((response) => {
        this.emitters = response.result || [];
        this.emitters.forEach((e) => {
          if (!(e.id in this.mappings)) this.$set(this.mappings, e.id, this.emptyMapping());
        });
      });
      const p2 = generalService
        .filter({ is_resultado: true, imputable: true }, ConstantSections.SECTION_ACCOUNTS)
        .then((response) => { this.resultAccounts = response.result || []; });

      Promise.all([p1, p2]).finally(() => {
        this.loading_accounts = false;
        this.initAccountingSection();
      });
    },
    emptyMapping() {
      return { venta: null, compra: null };
    },
    // Lados que aplican según el tipo: Ingreso → venta, Egreso → compra, Ambos → los dos.
    showsSide(side) {
      const tipo = this.tipoId();
      if (tipo === "I") return side === "venta";
      if (tipo === "E") return side === "compra";
      return true;
    },
    accountsForEmitter(emitterId, side) {
      const grupoEsperado = side === "venta" ? 4 : 5;
      return this.resultAccounts.filter((a) => {
        if (!a.emitter || a.emitter.id !== emitterId) return false;
        const grupo = a.grupo ? a.grupo.id : null;
        return grupo === grupoEsperado;
      });
    },
    missingSideLabel() {
      const tipo = this.tipoId();
      if (tipo === "I") return "cuenta de venta (Ingresos)";
      if (tipo === "E") return "cuenta de compra (Egresos)";
      return "cuenta de venta o de compra";
    },
    onTipoChange() {
      // Limpia el lado que dejó de aplicar con el nuevo tipo
      this.emitters.forEach((e) => {
        const map = this.mappings[e.id];
        if (!map) return;
        if (!this.showsSide("venta")) map.venta = null;
        if (!this.showsSide("compra")) map.compra = null;
      });
    },
    initAccountingSection() {
      // Reinicia y precarga los mapeos existentes al abrir el modal (modo edición)
      this.emitters.forEach((e) => this.$set(this.mappings, e.id, this.emptyMapping()));
      if (this.item && this.item.id > 0) {
        generalService
          .filter({ concepto: this.item.id }, ConstantSections.SECTION_ACCOUNTS + "/conceptos")
          .then((response) => {
            (response.result || []).forEach((map) => {
              if (!map.emitter) return;
              this.$set(this.mappings, map.emitter.id, {
                venta: map.account_venta || null,
                compra: map.account_compra || null,
              });
            });
          });
      }
    },
    createItem(e) {
      if (this.loading) return;
      this.$validator.validate().then((valid) => {
        if (!valid) return;

        // Obligatorio para emisores con contabilidad habilitada: Ingreso → venta, Egreso → compra,
        // Ambos → al menos una de las dos.
        const missing = this.emitters.filter((em) => {
          if (!em.contabilidad_habilitada) return false;
          const map = this.mappings[em.id] || this.emptyMapping();
          const tipo = this.tipoId();
          if (tipo === "I") return !map.venta;
          if (tipo === "E") return !map.compra;
          return !map.venta && !map.compra;
        });
        if (missing.length > 0) {
          this.$toast.error(
            "Falta la " + this.missingSideLabel() + " en: " + missing.map((em) => em.name).join(", "),
            "Error"
          );
          return;
        }

        e.preventDefault();
        this.loading = true;
        this.service.setSection(ConstantSections.SECTION_CONCEPTOS);

        const payload = {
          id: this.item.id || null,
          name: this.item.name,
          tipo: this.item.tipo ? this.item.tipo.id : null,
          parent: this.item.parent ? { id: this.item.parent.id } : null,
          code: this.item.code || null,
          enabled: this.item.enabled,
          accounts: this.emitters.map((em) => {
            const map = this.mappings[em.id] || this.emptyMapping();
            return {
              emitter_id: em.id,
              account_venta_id: this.showsSide("venta") && map.venta ? map.venta.id : null,
              account_compra_id: this.showsSide("compra") && map.compra ? map.compra.id : null,
            };
          }),
        };

        const call = this.item.id > 0 ? this.service.update(payload) : this.service.create(payload);
        call
          .then((response) => {
            if (response.status == true) {
              const verb = this.item.id > 0 ? "modifico" : "creo";
              this.$toast.success("El " + this.modal_title + " se " + verb + " correctamente.", "OK");
              this.$emit("updateDialog", false);
              this.$emit("loadList");
            } else {
              this.$toast.error(response.msg || response.message, "Error");
            }
          })
          .catch(() => {
            this.$toast.error("Ocurrió un error al guardar.", "Error");
          })
          .finally(() => (this.loading = false));
      });
    },
  },
};
</script>
